import React, { useState, useEffect } from 'react';
import { X, Calendar, Loader2 } from 'lucide-react';
import { colors } from '../styles/colors';
import ModalPortal from './ModalPortal';
import { DatePicker } from './ui/DatePicker';

/**
 * Modale générique pour sélectionner une date
 * @param {Object} props
 * @param {boolean} props.isOpen - Modale ouverte ou fermée
 * @param {Function} props.onClose - Fermer la modale
 * @param {Function} props.onConfirm - Callback avec la date confirmée (peut être async)
 * @param {string} props.currentDate - Date actuelle (YYYY-MM-DD)
 * @param {string} props.title - Titre de la modale
 * @param {string} props.description - Description en dessous du titre
 * @param {string} props.confirmButtonText - Texte du bouton de confirmation
 * @param {boolean} props.showTodayOption - Afficher l'option "Date du jour" (défaut: true)
 * @param {string} props.maxDate - Date maximum sélectionnable (YYYY-MM-DD)
 */
function DateSelectionModal({
  isOpen,
  onClose,
  onConfirm,
  currentDate,
  title = "Sélectionner une date",
  description,
  confirmButtonText = "Confirmer",
  showTodayOption = true,
  maxDate
}) {
  const today = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(currentDate || today);
  const [useTodayDate, setUseTodayDate] = useState(showTodayOption);
  const [loading, setLoading] = useState(false);

  // Ce composant reste monté en permanence par ses appelants (rendu avec isOpen plutôt que
  // démonté), donc le useState ci-dessus ne capture currentDate qu'une seule fois, au tout
  // premier montage. Sans ce useEffect, une réouverture ultérieure avec un currentDate
  // différent (ex: une autre facture) resterait figée sur l'ancienne valeur.
  useEffect(() => {
    if (isOpen) {
      setSelectedDate(currentDate || today);
      setUseTodayDate(showTodayOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentDate]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    const finalDate = (useTodayDate && showTodayOption) ? today : selectedDate;

    setLoading(true);
    try {
      await onConfirm(finalDate);
    } catch (error) {
      console.error('Erreur lors de la confirmation:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <ModalPortal>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
      <div style={{
        backgroundColor: colors.background.white,
        borderRadius: '0.5rem',
        width: '90%',
        maxWidth: '500px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1.5rem',
          borderBottom: `1px solid ${colors.border.light}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Calendar size={24} color={colors.primary.teal} />
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              color: colors.text.primary,
              margin: 0
            }}>
              {title}
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderRadius: '0.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} color={colors.text.secondary} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.5rem' }}>
          {description && (
            <p style={{
              color: colors.text.secondary,
              marginBottom: '1.5rem',
              fontSize: '0.95rem'
            }}>
              {description}
            </p>
          )}

          {/* Option 1: Date du jour */}
          {showTodayOption && (
            <div
              onClick={() => setUseTodayDate(true)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                padding: '1rem',
                border: `2px solid ${useTodayDate ? colors.primary.teal : colors.border.light}`,
                borderRadius: '0.5rem',
                marginBottom: '1rem',
                cursor: 'pointer',
                backgroundColor: useTodayDate ? colors.background.light : 'transparent',
                transition: 'all 0.2s'
              }}
            >
              <input
                type="radio"
                checked={useTodayDate}
                onChange={() => setUseTodayDate(true)}
                style={{
                  marginTop: '0.25rem',
                  cursor: 'pointer',
                  accentColor: colors.primary.teal
                }}
              />
              <div style={{ flex: 1 }}>
                <div style={{
                  fontWeight: '600',
                  color: colors.text.primary,
                  marginBottom: '0.25rem'
                }}>
                  Date d'aujourd'hui
                </div>
                <div style={{
                  fontSize: '0.875rem',
                  color: colors.text.secondary
                }}>
                  {formatDateForDisplay(today)}
                </div>
              </div>
            </div>
          )}

          {/* Option 2: Date personnalisée */}
          <div
            onClick={() => setUseTodayDate(false)}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem',
              padding: '1rem',
              border: `2px solid ${!useTodayDate ? colors.primary.teal : colors.border.light}`,
              borderRadius: '0.5rem',
              cursor: 'pointer',
              backgroundColor: !useTodayDate ? colors.background.light : 'transparent',
              transition: 'all 0.2s'
            }}
          >
            {showTodayOption && (
              <input
                type="radio"
                checked={!useTodayDate}
                onChange={() => setUseTodayDate(false)}
                style={{
                  marginTop: '0.25rem',
                  cursor: 'pointer',
                  accentColor: colors.primary.teal
                }}
              />
            )}
            <div style={{ flex: 1 }}>
              <div style={{
                fontWeight: '600',
                color: colors.text.primary,
                marginBottom: '0.5rem'
              }}>
                {showTodayOption ? "Choisir une autre date" : "Sélectionner une date"}
              </div>
              <DatePicker
                value={selectedDate}
                max={maxDate}
                onChange={(v) => {
                  setSelectedDate(v);
                  setUseTodayDate(false);
                }}
              />
            </div>
          </div>

          {/* Aperçu de la date sélectionnée */}
          <div style={{
            marginTop: '1.5rem',
            padding: '1rem',
            backgroundColor: colors.background.light,
            borderRadius: '0.5rem',
            borderLeft: `4px solid ${colors.primary.teal}`
          }}>
            <div style={{
              fontSize: '0.875rem',
              color: colors.text.secondary,
              marginBottom: '0.25rem'
            }}>
              Date sélectionnée:
            </div>
            <div style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: colors.text.primary
            }}>
              {formatDateForDisplay((useTodayDate && showTodayOption) ? today : selectedDate)}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          padding: '1.5rem',
          borderTop: `1px solid ${colors.border.light}`,
          backgroundColor: colors.background.light
        }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '0.75rem 1.5rem',
              border: `1px solid ${colors.border.medium}`,
              backgroundColor: colors.background.white,
              color: colors.text.primary,
              borderRadius: '0.375rem',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = colors.background.light;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = colors.background.white;
            }}
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            style={{
              flex: 1,
              padding: '0.75rem 1.5rem',
              border: 'none',
              backgroundColor: colors.primary.teal,
              color: colors.text.white,
              borderRadius: '0.375rem',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              opacity: loading ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = colors.primary.tealDark;
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = colors.primary.teal;
              }
            }}
          >
            {loading && (
              <Loader2
                size={18}
                style={{
                  animation: 'spin 1s linear infinite'
                }}
              />
            )}
            {loading ? 'Traitement en cours...' : confirmButtonText}
            <style>{`
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}</style>
          </button>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}

export default DateSelectionModal;
